import { FC, PointerEvent as ReactPointerEvent, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_PICKER_HSV,
  Hsv,
  hexToHsv,
  hsvToHex,
} from "../../config/cardColors";
import { REDESIGN_NS } from "../../i18n";
import { useDismissable } from "../../ui/useDismissable";

interface Props {
  value: string | null;
  onChange: (next: string | null) => void;
}

const STEP = 0.02;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * Doygunluk/parlaklık karesi ve renk tonu kaydırıcısı. Hazır palet yerine serbest seçim:
 * renk zemine düşük oranda karıştığı için okunabilirlik seçilen renkten bağımsız.
 */
const ColorPicker: FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation(REDESIGN_NS);
  const rootRef = useRef<HTMLDivElement>(null);
  const squareRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hsv, setHsv] = useState<Hsv>(value ? hexToHsv(value) : DEFAULT_PICKER_HSV);

  const close = useCallback(() => setIsOpen(false), []);
  useDismissable(rootRef, isOpen, close);

  const apply = (next: Hsv) => {
    setHsv(next);
    onChange(hsvToHex(next));
  };

  /* Kare içinde konumdan doygunluk ve parlaklık okunur; y ekseni yukarı doğru parlaklık. */
  const applyFromPoint = (clientX: number, clientY: number) => {
    const box = squareRef.current?.getBoundingClientRect();
    if (!box || box.width === 0 || box.height === 0) return;

    apply({
      h: hsv.h,
      s: clamp01((clientX - box.left) / box.width),
      v: 1 - clamp01((clientY - box.top) / box.height),
    });
  };

  const onSquarePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    // Yakalama olmadan imleç kareden çıkınca sürükleme kopuyor.
    event.currentTarget.setPointerCapture(event.pointerId);
    applyFromPoint(event.clientX, event.clientY);
  };

  const onSquarePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    applyFromPoint(event.clientX, event.clientY);
  };

  const onSquareKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-STEP, 0],
      ArrowRight: [STEP, 0],
      ArrowUp: [0, STEP],
      ArrowDown: [0, -STEP],
    };
    const move = moves[event.key];
    if (!move) return;

    event.preventDefault();
    apply({ h: hsv.h, s: clamp01(hsv.s + move[0]), v: clamp01(hsv.v + move[1]) });
  };

  const hueColor = hsvToHex({ h: hsv.h, s: 1, v: 1 });

  return (
    <div className="sso-rd__color" ref={rootRef}>
      <button
        type="button"
        className="sso-rd__color-btn"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        title={t("Card color")}
        aria-label={t("Card color")}
      />

      {isOpen && (
        <div className="sso-rd__color-pop">
          <div
            ref={squareRef}
            className="sso-rd__color-square"
            /* `background` kısayolu satır içinde yazılırsa CSS'teki iki gradient'i de
               sıfırlar ve kare düz renge döner; yalnızca zemin rengi verilmeli. */
            style={{ backgroundColor: hueColor }}
            role="slider"
            tabIndex={0}
            aria-label={t("Saturation and brightness")}
            /* Alan iki eksenli; `valuenow` yatay ekseni (doygunluk) taşır, seçilen
               rengin kendisi ekran okuyucuya `valuetext` ile gider. */
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(hsv.s * 100)}
            aria-valuetext={hsvToHex(hsv)}
            onPointerDown={onSquarePointerDown}
            onPointerMove={onSquarePointerMove}
            onKeyDown={onSquareKeyDown}
          >
            <span
              className="sso-rd__color-thumb"
              style={{ left: hsv.s * 100 + "%", top: (1 - hsv.v) * 100 + "%" }}
            />
          </div>

          <input
            type="range"
            className="sso-rd__color-hue"
            min={0}
            max={359}
            value={Math.round(hsv.h)}
            onChange={(event) => apply({ ...hsv, h: Number(event.target.value) })}
            aria-label={t("Hue")}
          />

          <div className="sso-rd__color-foot">
            <span className="sso-rd__color-value">{hsvToHex(hsv)}</span>
            <button
              type="button"
              className="sso-rd__color-reset"
              onClick={() => {
                onChange(null);
                setIsOpen(false);
              }}
            >
              {t("Default")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
